// Product analytics (PostHog) - thin, always-safe wrappers.
//
// PostHog only initialises when VITE_PUBLIC_POSTHOG_KEY is set (main.tsx), so every helper here
// no-ops safely when analytics is off (local dev default). Import these instead of posthog directly:
// call sites stay one-liners and the no-init guard lives in one place.
//
// HARD RULE (see docs/PRD_POSTHOG_ANALYTICS.md): event properties carry ids and enums ONLY -
// never meeting titles, transcript/notes content, or attendee emails. Content-bearing UI surfaces
// are additionally masked from autocapture with the `ph-no-capture` class.

import posthog from 'posthog-js';

const enabled = () => Boolean((posthog as unknown as { __loaded?: boolean }).__loaded);

/** Curated product event (snake_case object_verb). Properties: ids/enums/counts only. */
export function track(event: string, properties?: Record<string, string | number | boolean | null>) {
  if (!enabled()) return;
  try {
    posthog.capture(event, properties);
  } catch {
    /* analytics must never break the app */
  }
}

/** Tie events to the signed-in user (Supabase user id as the stable distinct id). */
export function identifyUser(userId: string, props?: { email?: string | null; name?: string | null }) {
  if (!enabled() || !userId) return;
  try {
    posthog.identify(userId, {
      ...(props?.email ? { email: props.email } : {}),
      ...(props?.name ? { name: props.name } : {}),
    });
  } catch {
    /* no-op */
  }
}

/** Workspace/company group analytics - the workspace is the revenue unit. */
export function setAnalyticsGroups(groups: {
  workspaceId?: string | null;
  workspaceName?: string | null;
  companyId?: string | null;
  companyName?: string | null;
}) {
  if (!enabled()) return;
  try {
    if (groups.workspaceId) {
      posthog.group('workspace', groups.workspaceId, groups.workspaceName ? { name: groups.workspaceName } : undefined);
    }
    if (groups.companyId) {
      posthog.group('company', groups.companyId, groups.companyName ? { name: groups.companyName } : undefined);
    }
  } catch {
    /* no-op */
  }
}

/** Clear identity on sign-out (several humans here run multiple accounts - never merge them). */
export function resetAnalytics() {
  if (!enabled()) return;
  try {
    posthog.reset();
  } catch {
    /* no-op */
  }
}
